/**
 * The PWA's in-app surface (docs/PWA.md), mounted once in routes/__root.tsx:
 *
 *   UpdateToast     "Update ready": a new service worker is waiting; Reload hands over (SKIP_WAITING → reload)
 *   InstallBanner   "Install algo.boo" from the second visit on (Chromium prompt, or the iOS Share hint)
 *   OfflineBanner   the device is offline
 *   AppBadge        the home-screen badge shows the pending approvals of the signed-in user
 *   PushSync        keeps this browser's push subscription current (rotated VAPID key, another account)
 *   Standalone      `html[data-standalone]` in the installed app (styles hide browser-only UI)
 */
import { useEffect, useRef, useState } from 'react'
import { Button, Icon, IconButton } from '@algoboo/ui'
import { useThreads } from '../../lib/api/conversations'
import { applyUpdate, isStandalone, onUpdateReady } from '../../pwa/register'
import { INSTALL_DELAY_MS, countVisit, installDismissed, installOffer, isIosSafari, markInstallShown, onInstallPrompt, type BeforeInstallPromptEvent, type InstallOffer } from '../../pwa/install'
import { useSession } from '../session'
import { currentPushCapability, syncPush, useVapidKey } from '../social/data'

/** Toasts sit above the tab bar on phones and bottom-right from 768px. */
const TOAST = 'fixed inset-x-3 bottom-[calc(68px+env(safe-area-inset-bottom))] z-40 mx-auto max-w-[420px] rounded-xl border border-line bg-surface p-3 shadow-[var(--shadow-3)] md:inset-x-auto md:bottom-4 md:right-4 rh-sheet'

export function PwaHost() {
  return (
    <>
      <Standalone />
      <OfflineBanner />
      <UpdateToast />
      <InstallBanner />
      <AppBadge />
      <PushSync />
    </>
  )
}

function Standalone() {
  useEffect(() => {
    const apply = () => {
      if (isStandalone()) document.documentElement.dataset['standalone'] = '1'
      else delete document.documentElement.dataset['standalone']
    }
    apply()
    const mq = window.matchMedia?.('(display-mode: standalone)')
    mq?.addEventListener?.('change', apply)
    return () => mq?.removeEventListener?.('change', apply)
  }, [])
  return null
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false))
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  return online
}

function OfflineBanner() {
  const online = useOnline()
  if (online) return null
  return (
    <div role="status" className="fixed inset-x-0 top-[calc(56px+env(safe-area-inset-top))] z-30 flex items-center justify-center gap-2 border-b border-line bg-warn-soft px-4 py-1.5 text-[12.5px] text-ink backdrop-blur">
      <Icon name="offline" size={14} /> You are offline. Live data and signing resume when you reconnect.
    </div>
  )
}

function UpdateToast() {
  const [worker, setWorker] = useState<ServiceWorker | null>(null)
  const [dismissed, setDismissed] = useState(false)
  useEffect(() => onUpdateReady(setWorker), [])
  if (!worker || dismissed) return null
  return (
    <div role="status" aria-live="polite" data-testid="update-toast" className={TOAST}>
      <div className="flex items-center gap-3">
        <Icon name="refresh" size={16} className="shrink-0 text-accent" />
        <p className="min-w-0 flex-1 text-[13.5px] font-medium text-ink">Update ready</p>
        <Button size="sm" variant="primary" onClick={() => applyUpdate(worker)}>
          Reload
        </Button>
        <IconButton icon="close" label="Later" size={32} onClick={() => setDismissed(true)} />
      </div>
    </div>
  )
}

function InstallBanner() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null)
  const [offer, setOffer] = useState<InstallOffer>('none')
  const [ready, setReady] = useState(false)
  const visits = useRef(0)
  useEffect(() => {
    visits.current = countVisit()
    const off = onInstallPrompt(setEvt)
    const onInstalled = () => {
      markInstallShown('installed')
      setOffer('none')
    }
    window.addEventListener('appinstalled', onInstalled)
    const t = setTimeout(() => setReady(true), INSTALL_DELAY_MS)
    return () => {
      off()
      clearTimeout(t)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])
  useEffect(() => {
    if (!ready) return
    setOffer(
      installOffer({
        standalone: isStandalone(),
        dismissed: installDismissed(),
        visits: visits.current,
        hasPromptEvent: !!evt,
        iosSafari: isIosSafari(navigator.userAgent, navigator.maxTouchPoints ?? 0),
      }),
    )
  }, [ready, evt])
  if (offer === 'none') return null
  const dismiss = (outcome: string) => {
    markInstallShown(outcome)
    setOffer('none')
  }
  return (
    <div role="region" aria-label="Install algo.boo" data-testid="install-banner" data-offer={offer} className={`${TOAST} rh-browser-only`}>
      <div className="flex items-center gap-3">
        <Icon name="download" size={16} className="shrink-0 text-ink-2" />
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-medium text-ink">Install algo.boo</p>
          <p className="text-[12px] text-ink-3">
            {offer === 'ios' ? (
              <>
                Tap <b>Share</b>, then <b>Add to Home Screen</b>. Push and the badge work in the installed app.
              </>
            ) : (
              'Full screen, one tap away, with push for approvals.'
            )}
          </p>
        </div>
        {offer === 'prompt' && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              const e = evt
              dismiss('prompted')
              if (!e) return
              void e
                .prompt()
                .then(() => e.userChoice)
                .then((c) => markInstallShown(c.outcome))
                .catch(() => undefined)
            }}
          >
            Install
          </Button>
        )}
        <IconButton icon="close" label="Not now" size={32} onClick={() => dismiss('dismissed')} />
      </div>
    </div>
  )
}

function badgeSupported(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator
}

function notificationsGranted(): boolean {
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted'
  } catch {
    return false
  }
}

/** Pending approvals (the thread list's `pendingApprovals`) on the app icon; cleared when there are none. */
function AppBadge() {
  const { me } = useSession()
  // The badge is visible only on an installed app (or with notifications on); elsewhere no extra request is made.
  const enabled = !!me && badgeSupported() && (isStandalone() || notificationsGranted())
  const threads = useThreads('open', enabled)
  const count = enabled && threads.data ? threads.data.items.reduce((n, t) => n + t.pendingApprovals, 0) : null
  useEffect(() => {
    if (!badgeSupported()) return
    const nav = navigator as Navigator & { setAppBadge(n?: number): Promise<void>; clearAppBadge(): Promise<void> }
    if (!me) void nav.clearAppBadge().catch(() => undefined)
    else if (count !== null) void (count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge()).catch(() => undefined)
  }, [me, count])
  return null
}

/** Once per signed-in account and server key: keep an existing push subscription current (never prompts). */
function PushSync() {
  const { me } = useSession()
  const enabled = !!me && currentPushCapability() === 'ok' && notificationsGranted()
  const key = useVapidKey(enabled)
  const done = useRef<string | null>(null)
  const publicKey = key.data?.publicKey ?? null
  const keyId = key.data?.keyId ?? null
  const userId = me?.user.id ?? null
  useEffect(() => {
    if (!enabled || !publicKey || !userId) return
    const mark = `${userId}:${keyId ?? publicKey}`
    if (done.current === mark) return
    done.current = mark
    void syncPush({ publicKey, keyId }).catch(() => undefined)
  }, [enabled, publicKey, keyId, userId])
  return null
}
